// Axios instance shared by every API call from the SPA.
//
// Auth model (per Phase 2 backend):
//   - Access token: in-memory only (no localStorage — XSS-stealable
//     persistence is the entire reason for the refresh-cookie pattern).
//     Sent via `Authorization: Bearer ...` header.
//   - Refresh token: HttpOnly cookie at /api/auth/refresh,
//     SameSite=Strict. We rely on the browser to send it; we never
//     read it from JS.
//
// On a 401, the response interceptor attempts a single refresh and
// replays the original request. If the refresh also fails, we clear
// state and surface the 401 — the calling page routes the user back
// to /login.
//
// Which of the two refresh cookie paths a tab uses is the one piece of session
// state that is persisted rather than held in memory — see impersonationMode.

import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import type { CustomerUserDto, ImpersonationContextDto } from 'shared';

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

// Which cookie path this tab's session refreshes through:
//   - false (default): /api/auth/refresh + pp_refresh cookie
//   - true:            /api/auth/impersonation/refresh + pp_imp_refresh cookie
// Set by api/impersonation.ts when the in-memory access token is an
// impersonation token; cleared on login / logout / end-impersonation.
//
// This flag has to OUTLIVE the in-memory access token. Both refresh cookies are
// HttpOnly and neither is readable from JS, so after a reload the SPA has no way
// to tell which kind of session the browser still holds — and guessing wrong
// means asking /api/auth/refresh for a session whose cookie is scoped to
// /api/auth/impersonation, getting a 401, and signing a rep out mid-session.
// So it is mirrored into sessionStorage: per-tab, which is the scope an
// impersonation session actually has, and it survives a reload. Only the KIND of
// session is stored — never a token, which is exactly what the in-memory access
// token model exists to avoid.
const SESSION_KIND_KEY = 'pp.session-kind.impersonation';

// sessionStorage can throw (private mode, storage disabled). Treat any failure as
// "no hint available": an impersonation in such a browser is simply not resumable
// across a reload, which is a degraded session and not a broken app.
function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

let impersonationMode = sessionStore()?.getItem(SESSION_KIND_KEY) === '1';

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setImpersonationMode(on: boolean): void {
  impersonationMode = on;
  const store = sessionStore();
  if (!store) return;
  try {
    if (on) store.setItem(SESSION_KIND_KEY, '1');
    else store.removeItem(SESSION_KIND_KEY);
  } catch {
    // Quota / disabled storage — the in-memory flag still governs this page.
  }
}

// The auth context registers a hook so the interceptor can route the
// user back to login when the refresh fails. Avoids a circular import.
export function setOnAuthLost(cb: (() => void) | null): void {
  onAuthLost = cb;
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  // Send the refresh cookie on /api/auth/refresh + /api/auth/logout.
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Respect an Authorization header the caller set explicitly — the
  // employee-surface calls (api/employeeJobs.ts, the employee picker in
  // api/impersonation.ts) pass the employee JWT this way, and it lives in a
  // SEPARATE in-memory slot from `accessToken`. Only fall back to the
  // in-memory customer/impersonation token when the request didn't bring its
  // own; otherwise an active impersonation token would clobber the employee
  // JWT and every /api/employee/* call would 401.
  if (accessToken && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// What a successful refresh yields. `impersonation` is present only when the
// session that came back is an impersonation, so one caller can rehydrate
// either kind from one response.
export type RefreshedSession = {
  access_token: string;
  access_token_expires_in: number;
  user: CustomerUserDto;
  impersonation?: ImpersonationContextDto | null;
};

// THE refresh chokepoint. Every refresh in the SPA goes through this one
// function, and there is exactly one in-flight promise for the whole app.
//
// This is a correctness requirement, not tidiness. The refresh cookie is
// single-use with rotation and theft detection (backend refresh-tokens.ts):
// two requests that present the SAME cookie make the second look like a
// replay, which burns the whole family and signs the session out. Before this
// was centralized there were three entry points — the 401 interceptor below and
// one apiece in api/auth.ts and api/impersonation.ts — each with its own
// in-flight guard, or in the impersonation case none at all. A guard per module
// does not dedupe across modules: a boot-time resume racing a 401 retry
// presented the same cookie twice and burned the family minutes into a support
// call. Add a new refresh call site by calling THIS, never by posting to
// /auth/refresh directly.
let refreshInFlight: Promise<RefreshedSession | null> | null = null;

function statusOf(err: unknown): number | undefined {
  return (err as AxiosError | undefined)?.response?.status;
}

// Bare axios, not `api`: the response interceptor must not see (and try to
// recover from) the refresh call itself.
async function postRefresh(url: string): Promise<RefreshedSession> {
  const res = await axios.post<RefreshedSession>(url, {}, { withCredentials: true });
  return res.data;
}

export function refreshSession(): Promise<RefreshedSession | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      // ADR-008: the customer cookie (/api/auth) and the impersonation cookie
      // (/api/auth/impersonation) are scoped to different paths, so each
      // request only carries the matching one. The tab's persisted session-kind
      // hint is the only thing that survives a reload to say which we hold.
      const url = impersonationMode
        ? '/api/auth/impersonation/refresh'
        : '/api/auth/refresh';
      // 409 REFRESH_RACE: another tab presented the same cookie first and the
      // server refused rather than forking the chain. The winning response
      // already put the current cookie in the jar, so retrying presents the
      // live token. Bounded at two retries so three tabs racing the same cookie
      // still converge, and so a persistent 409 can never become a storm.
      for (let attempt = 0; ; attempt += 1) {
        try {
          const session = await postRefresh(url);
          accessToken = session.access_token;
          return session;
        } catch (err) {
          const status = statusOf(err);
          if (status === 409 && attempt < 2) continue;
          // A 401 on the impersonation path means that session is genuinely
          // over. Clear the hint so the NEXT load takes the customer path — but
          // do not silently continue into it here: falling through would sign
          // the tab in as whatever unrelated customer cookie the browser still
          // holds, with no re-auth and nothing in the UI to say the
          // impersonation ended. Anything else (network blip, 5xx) leaves the
          // hint alone; downgrading on a transient error is what stranded reps
          // on the wrong cookie path.
          if (status === 401 && impersonationMode) setImpersonationMode(false);
          return null;
        }
      }
    } catch {
      return null;
    } finally {
      // Released once this attempt settles. A caller arriving afterwards starts
      // a fresh attempt and presents the rotated cookie, so it is not a replay;
      // a caller that raced this one already holds this promise. The 409 retry
      // above is the backstop for the interleaving neither of those covers.
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const status = err.response?.status;
    const original = err.config as
      | (InternalAxiosRequestConfig & { __retried?: boolean })
      | undefined;

    // Only attempt a refresh on 401 from a non-auth endpoint, and
    // only once per original request.
    const isAuthEndpoint = original?.url?.startsWith('/auth/') ?? false;
    if (status !== 401 || !original || original.__retried || isAuthEndpoint) {
      return Promise.reject(err);
    }

    original.__retried = true;
    const session = await refreshSession();
    if (!session) {
      accessToken = null;
      onAuthLost?.();
      return Promise.reject(err);
    }
    if (original.headers) {
      original.headers.Authorization = `Bearer ${session.access_token}`;
    }
    return api.request(original);
  }
);

// Helper for surfacing the portal's envelope shape uniformly.
export type ApiErrorEnvelope = {
  error: {
    code: string;
    message: string;
    fallback_action?: string;
    details?: Record<string, unknown>;
  };
};

export function extractApiError(err: unknown): ApiErrorEnvelope['error'] | null {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof (err as { response: unknown }).response === 'object' &&
    (err as { response: { data?: unknown } }).response?.data &&
    typeof (err as { response: { data: { error?: unknown } } }).response.data === 'object' &&
    'error' in (err as { response: { data: { error?: unknown } } }).response.data
  ) {
    return (err as { response: { data: ApiErrorEnvelope } }).response.data.error;
  }
  return null;
}
